// F1 Teams Component
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { TableChart } from '../charts';

const TeamsComponent = ({ data }) => {
  const [activeView, setActiveView] = useState('overview');
  const [selectedYear, setSelectedYear] = useState(null);
  
  // Transform constructor data to make names clickable
  const transformedConstructors = data?.constructors?.map(constructor => ({
    ...constructor,
    name: (
      <Link 
        to={`/f1/teams/${constructor.constructorId}`}
        className="theme-primary hover:theme-accent hover:underline font-medium"
      >
        {constructor.name}
      </Link>
    )
  }));

  const renderTeamTable = () => {
    if (!data || !data.constructors || data.constructors.length === 0) {
      return (
        <div className="theme-bg-secondary rounded-lg p-4">
          <p className="theme-secondary">No constructor data available</p>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <div className="card">
          <h3 className="text-xl font-semibold theme-primary mb-4">All Teams</h3>
          <div className="mb-4 text-sm theme-secondary">
            Click on any constructor name to view detailed team information and performance history.
          </div>
          <TableChart data={transformedConstructors || []}
            config={{
              columns: [
                { key: 'name', label: 'Constructor' },
                { key: 'nationality', label: 'Nationality' },
                { key: 'championships', label: 'Championships' },
                { key: 'wins', label: 'Wins' },
                { key: 'poles', label: 'Poles' }
              ],
              sortable: true,
              searchable: true,
              sortBy: 'wins',
              sortDirection: 'desc',
              pagination: true,
              pageSize: 10,
              showHeader: true,
              rowHeight: 40,
              headerHeight: 40,
              showFooter: true,
              footerHeight: 40
            }}
          />
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="mt-8">
        {renderTeamTable()}
      </div>

      <div className="mt-8">
        <h3 className="text-xl font-semibold theme-primary mb-4">Team Categories</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="theme-bg-secondary rounded-lg p-4">
            <h4 className="font-semibold theme-primary mb-2">Top Teams</h4>
            <p className="theme-secondary text-sm">
              Elite constructors with multiple championships and consistent performance.
            </p>
          </div>
          <div className="theme-bg-secondary rounded-lg p-4">
            <h4 className="font-semibold theme-primary mb-2">Historic Teams</h4>
            <p className="theme-secondary text-sm">
              Legendary constructors from Formula 1 history and their achievements.
            </p>
          </div>
        </div>
      </div>

      <div className="mt-8">
        <h3 className="text-xl font-semibold theme-primary mb-4">Performance Metrics</h3>
        <div className="theme-bg-secondary rounded-lg p-4">
          <p className="theme-secondary">
            Team performance analysis including win rates, podium finishes, championship battles, 
            and comparative statistics between constructors.
          </p>
        </div>
      </div>
    </>
  );
};

export const teamsConfig = {
  title: 'F1 Teams',
  subtitle: 'Constructor information, team performance, and championship history from Formula 1',
  summary: ['Team statistics', 'Constructor championships', 'Performance analysis'],
  stats: [
    { 
      value: (data) => data?.constructors?.length || 0, 
      label: 'Active Constructors',
    },
    { 
      value: (data) => data?.constructors?.length || 0, 
      label: 'Historical Teams',
    },
    { 
      value: (data) => new Set(data?.constructors?.map(c => c.nationality)).size || 0, 
      label: 'Countries Represented',
      color: 'theme-primary'
    },
    { 
      value: (data) => data?.constructors?.filter(c => c.championships > 0).length || 0, 
      label: 'Constructor Champions',
    }
  ],
  component: TeamsComponent
};

export default TeamsComponent;
